import {
  getShiftForRow,
  isFullPunch,
  isOnTime,
  isLate,
  minutesLate,
  normalizeDate,
  eachDayInRange,
} from './attendanceSummaryAnalytics';

export const STANDARD_WORK_MINUTES = 480; // 8 hours

const val = (v) => (v === null || v === undefined ? '' : String(v).trim());

function timeToMinutes(t) {
  const s = val(t);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(mm)) return null;
  return h * 60 + mm;
}

function minutesToTime(mins) {
  if (mins === null || mins === undefined || Number.isNaN(mins)) return '';
  const m = Math.round(mins);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// Decimal hours, 2dp — payroll can multiply this directly.
const toHours = (mins) => Math.round(((mins || 0) / 60) * 100) / 100;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// yyyy-mm-dd HH:mm:ss — used for the "Generated" line on screen and in Excel.
export function formatDateTime(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
}

export function weekdayOf(isoDate) {
  const d = normalizeDate(isoDate);
  if (!d) return '';
  const parsed = new Date(`${d}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '' : DAY_NAMES[parsed.getDay()];
}

// Worked minutes for a single row (null when it can't be trusted).
export function workedMinutes(row) {
  if (!isFullPunch(row)) return null;
  const inM = timeToMinutes(row.InTime);
  const outM = timeToMinutes(row.OutTime);
  if (inM === null || outM === null) return null;
  const diff = outM - inM;
  return diff > 0 ? diff : null; // out-before-in is a data error, not 0 hours
}

// Minutes worked past the employee's shift end.
export function overtimeMinutes(row) {
  if (!isFullPunch(row)) return 0;
  const outM = timeToMinutes(row.OutTime);
  const endM = timeToMinutes(getShiftForRow(row).end);
  if (outM === null || endM === null) return 0;
  return Math.max(0, outM - endM);
}

// Minutes left before the shift ended.
export function earlyLeaveMinutes(row) {
  if (!isFullPunch(row)) return 0;
  const outM = timeToMinutes(row.OutTime);
  const endM = timeToMinutes(getShiftForRow(row).end);
  if (outM === null || endM === null) return 0;
  return Math.max(0, endM - outM);
}

export function exceptionOf(row) {
  const hasIn = !!val(row.InTime);
  const hasOut = !!val(row.OutTime);
  if (!hasIn && !hasOut) return 'No punch';
  if (!hasOut) return 'Missing Out';
  if (!hasIn) return 'Missing In';
  const inM = timeToMinutes(row.InTime);
  const outM = timeToMinutes(row.OutTime);
  if (inM !== null && outM !== null && outM <= inM) return 'Out before In';
  return '';
}

export function buildEmployeeReport(rows, range) {
  const periodDays = range ? eachDayInRange(range.from, range.to).length : 0;
  const byEmployee = new Map();

  rows.forEach((row) => {
    const key = val(row.EmployeeID) || val(row.ServiceNo);
    if (!key) return;

    if (!byEmployee.has(key)) {
      byEmployee.set(key, {
        employeeId: key,
        serviceNo: val(row.ServiceNo),
        name: val(row.Name) || `${val(row.FirstName)} ${val(row.LastName)}`.trim(),
        location: val(row.LocationName),
        designation: val(row.DesignationName),
        grade: val(row.GradeName),
        category: val(row.EmploymentCategoryName),
        shift: getShiftForRow(row).label,
        dates: new Set(),
        weekdayDays: 0,
        saturdayDays: 0,
        sundayDays: 0,
        fullPunchDays: 0,
        singlePunchDays: 0,
        onTimeDays: 0,
        lateDays: 0,
        lateMinutes: 0,
        earlyLeaveDays: 0,
        earlyLeaveMinutes: 0,
        workMinutes: 0,
        otMinutes: 0,
        shortMinutes: 0,
        inMinutesList: [],
        outMinutesList: [],
      });
    }

    const e = byEmployee.get(key);
    const date = normalizeDate(row.AttDate);
    if (date) e.dates.add(date);

    const day = weekdayOf(date);
    if (day === 'Saturday') e.saturdayDays += 1;
    else if (day === 'Sunday') e.sundayDays += 1;
    else if (day) e.weekdayDays += 1;

    if (isFullPunch(row)) e.fullPunchDays += 1;
    else e.singlePunchDays += 1;

    if (isOnTime(row)) e.onTimeDays += 1;
    if (isLate(row)) {
      e.lateDays += 1;
      e.lateMinutes += minutesLate(row);
    }

    const early = earlyLeaveMinutes(row);
    if (early > 0) {
      e.earlyLeaveDays += 1;
      e.earlyLeaveMinutes += early;
    }

    const worked = workedMinutes(row);
    if (worked !== null) {
      e.workMinutes += worked;
      e.otMinutes += overtimeMinutes(row);
      e.shortMinutes += Math.max(0, STANDARD_WORK_MINUTES - worked);
    }

    const inM = timeToMinutes(row.InTime);
    const outM = timeToMinutes(row.OutTime);
    if (inM !== null) e.inMinutesList.push(inM);
    if (outM !== null) e.outMinutesList.push(outM);
  });

  const avg = (list) => (list.length ? list.reduce((a, b) => a + b, 0) / list.length : null);

  return Array.from(byEmployee.values())
    .map((e) => {
      const sorted = Array.from(e.dates).sort((a, b) => a.localeCompare(b));
      const daysPresent = sorted.length;
      const normalMinutes = Math.max(0, e.workMinutes - e.otMinutes);

      return {
        serviceNo: e.serviceNo,
        name: e.name,
        location: e.location,
        designation: e.designation,
        grade: e.grade,
        category: e.category,
        shift: e.shift,

        daysPresent,
        weekdayDays: e.weekdayDays,
        saturdayDays: e.saturdayDays,
        sundayDays: e.sundayDays,
        firstAttendance: sorted[0] || '',
        lastAttendance: sorted[sorted.length - 1] || '',

        totalWorkHours: toHours(e.workMinutes),
        normalHours: toHours(normalMinutes),
        otHours: toHours(e.otMinutes),
        shortHours: toHours(e.shortMinutes),
        avgWorkHoursPerDay: e.fullPunchDays ? toHours(e.workMinutes / e.fullPunchDays) : 0,

        onTimeDays: e.onTimeDays,
        lateDays: e.lateDays,
        totalLateMinutes: Math.round(e.lateMinutes),
        earlyLeaveDays: e.earlyLeaveDays,
        totalEarlyLeaveMinutes: Math.round(e.earlyLeaveMinutes),
        avgInTime: minutesToTime(avg(e.inMinutesList)),
        avgOutTime: minutesToTime(avg(e.outMinutesList)),

        fullPunchDays: e.fullPunchDays,
        singlePunchDays: e.singlePunchDays,
        payrollReady: e.singlePunchDays === 0 ? 'Yes' : 'No',

        periodDays,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Column definitions — shared by the on-screen table and the Excel export so
export const REPORT_COLUMNS = [
  { key: 'serviceNo',              label: 'Service No',        group: 'Employee', width: 12 },
  { key: 'name',                   label: 'Employee Name',     group: 'Employee', width: 24 },
  { key: 'location',               label: 'Location',          group: 'Employee', width: 20 },
  { key: 'designation',            label: 'Designation',       group: 'Employee', width: 24 },
  { key: 'grade',                  label: 'Grade',             group: 'Employee', width: 16 },
  { key: 'category',               label: 'Category',          group: 'Employee', width: 14 },
  { key: 'shift',                  label: 'Shift',             group: 'Employee', width: 15 },

  { key: 'daysPresent',            label: 'Days Present',      group: 'Days', numeric: true, width: 12 },
  { key: 'weekdayDays',            label: 'Weekdays',          group: 'Days', numeric: true, width: 10 },
  { key: 'saturdayDays',           label: 'Saturdays',         group: 'Days', numeric: true, width: 10 },
  { key: 'sundayDays',             label: 'Sundays',           group: 'Days', numeric: true, width: 10 },
  { key: 'firstAttendance',        label: 'First Attendance',  group: 'Days', width: 15 },
  { key: 'lastAttendance',         label: 'Last Attendance',   group: 'Days', width: 15 },

  { key: 'totalWorkHours',         label: 'Total Work Hrs',    group: 'Hours', numeric: true, width: 13 },
  { key: 'normalHours',            label: 'Normal Hrs',        group: 'Hours', numeric: true, width: 12 },
  { key: 'otHours',                label: 'OT Hrs',            group: 'Hours', numeric: true, width: 10 },
  { key: 'shortHours',             label: 'Short Hrs',         group: 'Hours', numeric: true, width: 11 },
  { key: 'avgWorkHoursPerDay',     label: 'Avg Hrs / Day',     group: 'Hours', numeric: true, width: 12 },

  { key: 'onTimeDays',             label: 'On Time Days',      group: 'Punctuality', numeric: true, width: 12 },
  { key: 'lateDays',               label: 'Late Days',         group: 'Punctuality', numeric: true, width: 10 },
  { key: 'totalLateMinutes',       label: 'Late (min)',        group: 'Punctuality', numeric: true, width: 11 },
  { key: 'earlyLeaveDays',         label: 'Early Leave Days',  group: 'Punctuality', numeric: true, width: 15 },
  { key: 'totalEarlyLeaveMinutes', label: 'Early Leave (min)', group: 'Punctuality', numeric: true, width: 16 },
  { key: 'avgInTime',              label: 'Avg In',            group: 'Punctuality', width: 9 },
  { key: 'avgOutTime',             label: 'Avg Out',           group: 'Punctuality', width: 9 },

  { key: 'fullPunchDays',          label: 'Full Punch Days',   group: 'Data Quality', numeric: true, width: 14 },
  { key: 'singlePunchDays',        label: 'Single Punch Days', group: 'Data Quality', numeric: true, width: 16 },
  { key: 'payrollReady',           label: 'Payroll Ready',     group: 'Data Quality', width: 13 },
];

// Totals row for the on-screen table and the Excel footer.
export function buildReportTotals(report) {
  const sum = (key) => report.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
  const round2 = (n) => Math.round(n * 100) / 100;

  return {
    serviceNo: '',
    name: `TOTAL — ${report.length} employee${report.length === 1 ? '' : 's'}`,
    location: '', designation: '', grade: '', category: '', shift: '',
    daysPresent: sum('daysPresent'),
    weekdayDays: sum('weekdayDays'),
    saturdayDays: sum('saturdayDays'),
    sundayDays: sum('sundayDays'),
    firstAttendance: '', lastAttendance: '',
    totalWorkHours: round2(sum('totalWorkHours')),
    normalHours: round2(sum('normalHours')),
    otHours: round2(sum('otHours')),
    shortHours: round2(sum('shortHours')),
    avgWorkHoursPerDay: '',
    onTimeDays: sum('onTimeDays'),
    lateDays: sum('lateDays'),
    totalLateMinutes: sum('totalLateMinutes'),
    earlyLeaveDays: sum('earlyLeaveDays'),
    totalEarlyLeaveMinutes: sum('totalEarlyLeaveMinutes'),
    avgInTime: '', avgOutTime: '',
    fullPunchDays: sum('fullPunchDays'),
    singlePunchDays: sum('singlePunchDays'),
    payrollReady: '',
  };
}